<template>
  <div
    class="modal-body call-panel-body"
    style="flex: 1; overflow: hidden; display: flex; flex-direction: column"
  >
    <div class="whatsapp-layout-with-sidebar">
      <template v-if="isChannelsLoading">
        <div class="whatsapp-loading-state">
          <LoadingBars size="sm" />
        </div>
      </template>
      <template v-else-if="!whatsAppChannel?.isConnected || isWhatsAppChannelDisconnected">
        <div class="whatsapp-disconnected-state">
          <div class="whatsapp-disconnected-content">
            <Icon icon="mdi:link-variant-off" class="whatsapp-disconnected-icon" />
            <h3 class="whatsapp-disconnected-title">Not Connected</h3>
            <p class="whatsapp-disconnected-message">
              Please connect your WhatsApp account to view conversations.
            </p>
            <button class="whatsapp-disconnected-action" @click="$emit('reconnect-requested')">
              <Icon icon="mdi:link" />
              Connect Account
            </button>
          </div>
        </div>
      </template>
      <div v-else class="whatsapp-content-wrapper">
        <OutreachCueCards
          :icp-options="whatsAppICPOptions"
          :icp-loading="props.icpLoading"
          :contact-id="selectedBucket?.contact?.id || ''"
          :contact-name="
            [selectedBucket?.contact?.fname, selectedBucket?.contact?.lname]
              .filter(Boolean)
              .join(' ')
          "
          :full-width="true"
          @use-content="handleUseCueCardContent"
        />
        <div v-if="!showWhatsAppDetailView" class="whatsapp-sidebar-view">
          <div v-if="whatsAppChannel" class="whatsapp-sidebar-header">
            <Button
              variant="primary"
              size="sm"
              icon="mdi:chat-plus"
              @click="showStartChatModal = true"
              block
              :disabled="!isChannelHealthyStatus(whatsAppChannel?.status)"
            >
              Start New Chat
            </Button>
          </div>
          <div
            v-if="!isLoadingConversations && whatsAppConversations.length === 0"
            class="whatsapp-empty-state whatsapp-empty-state--no-conversations"
          >
            <div class="whatsapp-empty-state__content">
              <Icon icon="mdi:whatsapp" class="whatsapp-empty-state__icon" />
              <h3 class="whatsapp-empty-state__title">No Conversations Found</h3>
              <p class="whatsapp-empty-state__message">
                Start a conversation with this contact to view messages here.
              </p>
            </div>
          </div>
          <ConversationList
            v-else
            :conversations="whatsAppConversations as any"
            :selected-conversation-id="selectedWhatsAppConversationId ?? undefined"
            :is-loading="isLoadingConversations"
            type="whatsapp"
            :show-search="false"
            @conversation-selected="handleWhatsAppConversationSelected"
          />
        </div>
        <div v-else class="whatsapp-main-content">
          <div class="whatsapp-message-view-wrapper">
            <WhatsAppThreadCard
              v-if="computedSelectedWhatsAppConversation"
              :contact-name="
                (computedSelectedWhatsAppConversation as any)?.contactName ||
                (computedSelectedWhatsAppConversation as any)?.title
              "
              :last-seen="computedSelectedWhatsAppConversation.lastSeen || 'Last seen recently'"
              :unipile-messages="whatsappMessages"
              :is-loading="isLoadingMessages"
              :is-loading-older-messages="isLoadingOlderMessages"
              :has-more-messages="hasMoreMessages"
              :show-back-button="true"
              :highlight-message-id="props.initialMessageId"
              @back="handleWhatsAppBackToList"
              @reply="handleWhatsAppReply"
              @ai-reply="handleWhatsAppAiReply"
              @add-followup="handleWhatsAppAiFollowup"
              @forward="handleWhatsAppForward"
              @delete="handleDeleteMessage"
              @react="handleReactToMessage"
              @remove-reaction="handleRemoveReaction"
              @load-older-messages="handleLoadOlderMessages"
            />
            <div v-else class="whatsapp-empty-state">
              <p>Select a conversation to view messages</p>
            </div>
            <MessageComposer
              v-if="computedSelectedWhatsAppConversation"
              ref="messageComposerRef"
              :conversation="computedSelectedWhatsAppConversation"
              :reply-to="replyingTo"
              :channel-status="
                whatsAppChannel && whatsAppChannel.status ? whatsAppChannel.status : undefined
              "
              :is-sending="isSendingMessage"
              :show-ai-buttons="true"
              :ai-buttons-disabled="
                !isChannelHealthyStatus(whatsAppChannel?.status) || isUsageBlocked
              "
              :is-generating-a-i="isGeneratingAI"
              :generating-action="generatingAction"
              :contact-id="selectedBucket?.contact?.id"
              :icp-id="icps?.find((i) => i.isDefault)?.id"
              :icps="icps"
              :channel-name="'whatsapp'"
              @send-message="handleSendMessage"
              @cancel-reply="replyingTo = null"
              @ai-reply="handleWhatsAppAiReply"
              @ai-followup="handleWhatsAppAiFollowup"
              @ai-compose="handleWhatsAppAiCompose"
            />
          </div>
        </div>
      </div>

      <StartWhatsAppChatModal
        :show="showStartChatModal"
        :channels="availableChannelsForChat"
        :default-account-id="whatsAppChannel?.accountId"
        :is-loading="isCreatingChat"
        :auto-set-channel-type="'whatsapp'"
        :hide-account-selection="true"
        :contact-phone-numbers="contactPhoneNumbers"
        :initial-message="initialModalMessage"
        :contact-id="selectedBucket?.contact?.id"
        :icp-id="icps?.find((i) => i.isDefault)?.id"
        @close="
          showStartChatModal = false;
          initialModalMessage = '';
        "
        @create-chat="handleCreateChat"
      />
      <ForwardMessageModal
        :show="showForwardModal"
        :conversations="forwardableConversations"
        :channel-type="'whatsapp'"
        :is-loading="isLoadingConversations"
        :is-loading-more="false"
        :has-more="false"
        @close="showForwardModal = false"
        @forward="handleForwardConfirm"
        @search="handleForwardSearch"
        @load-more="handleForwardLoadMore"
      />
      <DeleteConfirmationModal
        v-if="showDeleteConfirmation"
        entity="message"
        :delete-function="confirmDeleteMessage"
        @close-delete-confirmation-modal="handleDeleteConfirmationClose"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from "vue";
import { useToast } from "@/composables/ui/useToast";
import { useStore } from "vuex";
import { Icon } from "@iconify/vue";
import LoadingBars from "@/components/ui/LoadingBars.vue";
import ConversationList from "@/components/bucket/interactions/ConversationList.vue";
import WhatsAppThreadCard from "@/components/bucket/interactions/WhatsAppThreadCard.vue";
import MessageComposer from "@/components/socials/MessageComposer.vue";
import ForwardMessageModal from "@/components/socials/ForwardMessageModal.vue";
import StartWhatsAppChatModal from "@/components/socials/StartWhatsAppChatModal.vue";
import OutreachCueCards from "@/components/bucket/ai/OutreachCueCards.vue";
import DeleteConfirmationModal from "@/components/modal/DeleteConfirmationModal.vue";
import { Button } from "@/components/ui";
import Unipile from "@/services/unipile/unipile";
import { isBodyEmpty } from "@/utils/emailComposerHelpers";
import AIGenerationService from "@/services/ai/ai-generation";
import { useResolvedAiPrompts } from "@/composables/useAiPrompts";
import { normalizeWhatsAppMessages } from "@/utils/conversationNormalizer";

import { useContactData } from "@/composables/contacts/useContactData";
import { useBillingStatus } from "@/composables/billing/useBillingStatus";
import type { Conversation, Message, Channel } from "@/dtos/Unipile.dto";
import { useQueryClient } from "@tanstack/vue-query";
import {
  useWhatsAppConversations,
  useWhatsAppMessages,
  useSendWhatsAppMessage,
  useCreateWhatsAppChat,
  useForwardWhatsAppMessage,
  useWhatsAppMessageWebhooks,
  useDeleteWhatsAppMessage,
} from "@/composables/whatsapp";
import { useReactToUnipileMessage } from "@/composables/useReactToUnipileMessage";

// Define props
interface Props {
  selectedBucket: any;
  contact?: any;
  whatsAppChannel: Channel | null;
  icps: Array<{ id: string; name: string; isDefault?: boolean }>;
  isChannelsLoading?: boolean;
  icpLoading?: boolean;
  pendingCueCardContent?: any;
  initialConversationId?: string | null;
  initialMessageId?: string | null;
}

const props = withDefaults(defineProps<Props>(), {
  isChannelsLoading: false,
  icpLoading: false,
  pendingCueCardContent: null,
  initialConversationId: null,
  initialMessageId: null,
});

// Define emits
interface Emits {
  (e: "refresh-conversations"): void;
  (e: "message-sent"): void;
  (e: "reconnect-requested"): void;
  (
    e: "open-ai-personalization",
    payload: { defaultType: string; contactName?: string; icpName?: string }
  ): void;
  (e: "switch-channel", payload: any): void;
  (e: "content-applied"): void;
}

const emit = defineEmits<Emits>();

const queryClient = useQueryClient();
const $toast = useToast();
const resolvedAiPromptsQuery = useResolvedAiPrompts();

// Store
const store = useStore();

const { showUsageBasedFeaturesModalOnce, isUsageBlocked } = useBillingStatus();

// Local state

const selectedWhatsAppConversationId = ref<string | null>(null);
const showWhatsAppDetailView = ref(false);
const selectedWhatsAppConversation = ref<Conversation | null>(null);
const replyingTo = ref<Message | null>(null);
const forwardingMessage = ref<Message | null>(null);
const showForwardModal = ref(false);
const showStartChatModal = ref(false);
const initialModalMessage = ref("");
const isGeneratingAI = ref(false);
const generatingAction = ref<string | null>(null);
const messageComposerRef = ref<InstanceType<typeof MessageComposer> | null>(null);

// Computed params for queries
const conversationsParams = computed(() => {
  const contactId = props.selectedBucket?.contact?.id;
  const accountId = props.whatsAppChannel?.accountId;

  if (!contactId || !accountId) {
    return null;
  }

  return {
    contactId,
    accountId,
    limit: 50,
    contactName:
      `${props.selectedBucket?.contact?.fname || ""} ${props.selectedBucket?.contact?.lname || ""}`.trim(),
  };
});

const messagesParams = computed(() => {
  if (!selectedWhatsAppConversationId.value) {
    return null;
  }

  return {
    conversationId: selectedWhatsAppConversationId.value,
    limit: 10,
  };
});

// Queries
const {
  data: conversationsData,
  isLoading: isLoadingConversations,
  refetch: refetchConversations,
} = useWhatsAppConversations(conversationsParams);

const {
  messages: whatsappMessages,
  isLoadingMessages,
  isLoadingOlderMessages,
  hasMoreMessages,
  handleLoadOlderMessages,
} = useWhatsAppMessages(
  messagesParams,
  computed(() => computedSelectedWhatsAppConversation.value)
);

// Mutations
const { sendMessage, isSending: isSendingMessage } = useSendWhatsAppMessage({
  messages: whatsappMessages,
});
const { createChat, isCreating: isCreatingChat } = useCreateWhatsAppChat();
const { forwardMessage: forwardMessageMutation } = useForwardWhatsAppMessage();
const { deleteMessage, isDeleting: isDeletingMessage } = useDeleteWhatsAppMessage();
const { addReaction, removeReaction } = useReactToUnipileMessage();

// Computed properties
const whatsAppConversations = computed(() => conversationsData.value || []);

const computedSelectedWhatsAppConversation = computed(() => {
  if (selectedWhatsAppConversation.value) {
    return selectedWhatsAppConversation.value;
  }
  if (!selectedWhatsAppConversationId.value) {
    return whatsAppConversations.value?.[0] || null;
  }
  return (
    whatsAppConversations.value?.find((c) => c.id === selectedWhatsAppConversationId.value) ||
    whatsAppConversations.value?.[0] ||
    null
  );
});

const whatsAppICPOptions = computed(() => {
  const options = [
    {
      value: "",
      label: "Select ICP for WhatsApp",
    },
  ];
  props.icps.forEach((icp) => {
    options.push({
      value: icp.id,
      label: icp.isDefault ? `${icp.name} (Default)` : icp.name,
    });
  });
  return options;
});

const contactPhoneNumbers = computed(() => {
  const { computeContactPhoneNumbers } = useContactData(store);
  return computeContactPhoneNumbers({ contact: props.contact ?? props.selectedBucket?.contact });
});

const availableChannelsForChat = computed(() => {
  const channels: Channel[] = [];
  if (props.whatsAppChannel?.accountId) {
    channels.push({
      id: props.whatsAppChannel.id,
      name: "WhatsApp",
      type: "whatsapp" as const,
      icon: "mdi:whatsapp",
      accountId: props.whatsAppChannel.accountId,
      isConnected: isChannelHealthyStatus(props.whatsAppChannel?.status),
      status: props.whatsAppChannel.status,
    });
  }
  return channels;
});

const forwardableConversations = computed(() => {
  const conversations = whatsAppConversations.value || [];
  const currentConversationId = computedSelectedWhatsAppConversation.value?.id;
  return conversations.filter((conv) => conv.id !== currentConversationId);
});

const isWhatsAppChannelDisconnected = computed(() => {
  if (!props.whatsAppChannel) return true;
  if (!props.whatsAppChannel.isConnected) return true;
  const status = props.whatsAppChannel.status?.toUpperCase();
  return ["DISCONNECTED", "ERROR"].includes(status || "");
});

// Utility functions
function isChannelHealthyStatus(status?: string): boolean {
  if (!status) return false;
  const upperStatus = status.toUpperCase();
  return ["OK", "CREATION_SUCCESS", "RECONNECTED", "SYNC_SUCCESS"].includes(upperStatus);
}

// Methods
async function applyWhatsAppContent(content: string) {
  await nextTick();
  if (
    messageComposerRef.value &&
    typeof (messageComposerRef.value as any).setContent === "function"
  ) {
    (messageComposerRef.value as any).setContent(content);
  }
  $toast?.success("WhatsApp message loaded from cue card");
}

async function handleUseCueCardContent(payload: {
  channel: string;
  content: string;
  subject?: string;
}) {
  if (payload.channel === "whatsapp" && payload.content) {
    if (showWhatsAppDetailView.value && computedSelectedWhatsAppConversation.value) {
      await applyWhatsAppContent(payload.content);
    } else {
      initialModalMessage.value = payload.content;
      showStartChatModal.value = true;
    }
  } else {
    emit("switch-channel", payload);
  }
}

async function handleWhatsAppConversationSelected(conversation: any) {
  selectedWhatsAppConversationId.value = conversation.id;
  selectedWhatsAppConversation.value = {
    ...conversation,
    channel: "whatsapp",
    contactName: conversation.contactName || conversation.title || "Unknown",
  };
  showWhatsAppDetailView.value = true;

  const contactId = props.selectedBucket?.contact?.id;

  if (conversation.unreadCount > 0) {
    const prevUnread = conversation.unreadCount;
    const p = conversationsParams.value;
    const convQueryKey = p
      ? ["whatsapp-conversations", p.contactId, p.accountId, p.limit || 50]
      : null;

    if (convQueryKey) {
      queryClient.setQueryData<any[]>(convQueryKey, (old) => {
        if (!old) return old;
        return old.map((c: any) =>
          c.id === conversation.id ? { ...c, unreadCount: 0, unread_count: 0 } : c
        );
      });
    }

    if (contactId) {
      queryClient.setQueryData(["interactionCounts", contactId], (old: any) => {
        if (!old?.whatsapp) return old;
        return {
          ...old,
          whatsapp: {
            ...old.whatsapp,
            unread: Math.max(0, (old.whatsapp.unread || 0) - prevUnread),
          },
        };
      });
    }
  }

  // Always mark the chat read on open, regardless of what Unipile's freshly-fetched
  // conversation list reports as `unreadCount`. That value can be out of sync with our own
  // chat.unreadCount (which only ever increments on inbound-message webhooks) — gating this
  // call on it meant a chat Unipile already considered "read" would never trigger our own
  // reset, leaving the Activity column's count stuck forever. performChatAction is a safe
  // no-op when the chat is already marked read.
  try {
    await Unipile.performChatAction(conversation.id, {
      action: "setReadStatus",
      value: true,
    });
    // Buckets table's Activity column is a separate bulk query, keyed by the page's
    // contactIds — the backend already zeroes chat.unreadCount on setReadStatus, but that
    // query's cache is untouched by the optimistic updates above, so invalidate it too.
    queryClient.invalidateQueries({ queryKey: ["engagement-summary"] });
  } catch (error) {
    console.error("Failed to mark conversation as read:", error);
    queryClient.invalidateQueries({ queryKey: ["whatsapp-conversations"] });
    if (contactId) {
      queryClient.invalidateQueries({ queryKey: ["interactionCounts", contactId] });
    }
  }
}

function handleWhatsAppBackToList() {
  selectedWhatsAppConversationId.value = null;
  selectedWhatsAppConversation.value = null;
  showWhatsAppDetailView.value = false;
  refetchConversations();
}

function handleWhatsAppReply(message?: Message) {
  replyingTo.value = message || null;
}

async function handleWhatsAppAiReply(payload?: any) {
  if (!computedSelectedWhatsAppConversation.value || isGeneratingAI.value) return;

  isGeneratingAI.value = true;
  generatingAction.value = "reply";
  try {
    const conversationHistory = normalizeWhatsAppMessages(whatsappMessages.value || []);

    const contactId = props.selectedBucket?.contact?.id;
    const icpId = payload?.icpId ?? props.icps?.find((i) => i.isDefault)?.id;

    const response = await AIGenerationService.generateReply({
      contactId,
      icpId,
      channel: "whatsapp",
      conversationHistory,
    });

    // Populate composer using exposed method
    if (messageComposerRef.value) {
      (messageComposerRef.value as any).setContent?.(response.body);
    }

    $toast?.success("AI reply generated");
  } catch (error) {
    console.error("Failed to generate AI reply:", error);
    $toast?.error("Failed to generate AI response");
  } finally {
    isGeneratingAI.value = false;
    generatingAction.value = null;
  }
}

async function handleWhatsAppAiFollowup(payload?: any) {
  if (!computedSelectedWhatsAppConversation.value || isGeneratingAI.value) return;

  isGeneratingAI.value = true;
  generatingAction.value = "followup";
  try {
    const conversationHistory = normalizeWhatsAppMessages(whatsappMessages.value || []);

    const contactId = props.selectedBucket?.contact?.id;
    const icpId = payload?.icpId ?? props.icps?.find((i) => i.isDefault)?.id;

    const response = await AIGenerationService.generateFollowup({
      contactId,
      icpId,
      channel: "whatsapp",
      conversationHistory,
    });

    // Populate composer using exposed method
    if (messageComposerRef.value) {
      (messageComposerRef.value as any).setContent?.(response.body);
    }

    $toast?.success("AI follow-up generated");
  } catch (error) {
    console.error("Failed to generate AI follow-up:", error);
    $toast?.error("Failed to generate AI response");
  } finally {
    isGeneratingAI.value = false;
    generatingAction.value = null;
  }
}

async function handleWhatsAppAiCompose(payload?: any) {
  if (isGeneratingAI.value) return;

  isGeneratingAI.value = true;
  generatingAction.value = "compose";
  try {
    const conversationHistory = normalizeWhatsAppMessages(whatsappMessages.value || []);

    const contactId = props.selectedBucket?.contact?.id;
    const icpId = payload?.icpId ?? props.icps?.find((i) => i.isDefault)?.id;

    const response = await AIGenerationService.generateCompose({
      contactId,
      icpId,
      channel: "whatsapp",
      conversationHistory,
      customInstructions: payload?.customInstructions,
      aiPromptCompose: resolvedAiPromptsQuery.composeAiPrompt.value,
    });

    if (messageComposerRef.value) {
      (messageComposerRef.value as any).setContent?.(response.body);
    }

    $toast?.success("AI message composed");
  } catch (error) {
    console.error("Failed to generate AI compose:", error);
    $toast?.error("Failed to generate AI response");
  } finally {
    isGeneratingAI.value = false;
    generatingAction.value = null;
  }
}

function handleWhatsAppForward(message: Message) {
  forwardingMessage.value = message;
  showForwardModal.value = true;
}

async function handleForwardConfirm(chatId: string) {
  if (!forwardingMessage.value) return;

  try {
    if (
      forwardingMessage.value.channel !== "whatsapp" &&
      forwardingMessage.value.channel !== "linkedin"
    ) {
      return;
    }

    showForwardModal.value = false;
    const messageToForward = forwardingMessage.value;
    forwardingMessage.value = null;

    await forwardMessageMutation({
      messageId: messageToForward.id,
      chat_id: chatId,
    });

    $toast?.success("Message forwarded successfully");
  } catch (error: any) {
    console.error("Failed to forward message:", error);
    $toast?.error(error.response?.data?.message || "Failed to forward message");
  }
}

function handleForwardSearch(query: string) {
  // Placeholder
}

function handleForwardLoadMore() {
  // Placeholder
}

const showDeleteConfirmation = ref(false);
const messageToDelete = ref<Message | null>(null);

function handleDeleteMessage(message: Message) {
  messageToDelete.value = message;
  showDeleteConfirmation.value = true;
}

async function confirmDeleteMessage() {
  const message = messageToDelete.value;
  if (!message) return null;

  try {
    await deleteMessage(message.id);
    const idx = whatsappMessages.value.findIndex((m: any) => m.id === message.id);
    if (idx !== -1) whatsappMessages.value[idx].deleted = true;
    $toast?.success("Message deleted successfully");
    return true;
  } catch (error: any) {
    console.error("Failed to delete message:", error);
    $toast?.error(error.response?.data?.message || "Failed to delete message");
    throw error;
  }
}

function handleDeleteConfirmationClose() {
  showDeleteConfirmation.value = false;
  messageToDelete.value = null;
}

async function handleReactToMessage(message: Message, reaction: string) {
  try {
    const existing = whatsappMessages.value.find((m: any) => m.id === message.id);
    const ownReaction = (existing as any)?.reactions?.find((r: any) => r.is_sender);
    const isRemoving = ownReaction?.value === reaction;

    const reactions = isRemoving
      ? await removeReaction(message.id)
      : await addReaction(message.id, reaction);

    const idx = whatsappMessages.value.findIndex((m: any) => m.id === message.id);
    if (idx !== -1) {
      whatsappMessages.value[idx].reactions = reactions;
    }
  } catch (error: any) {
    console.error("Failed to react to message:", error);
    $toast?.error(error.response?.data?.message || "Failed to react to message");
  }
}

async function handleRemoveReaction(message: Message) {
  try {
    const reactions = await removeReaction(message.id);
    const idx = whatsappMessages.value.findIndex((m: any) => m.id === message.id);
    if (idx !== -1) {
      whatsappMessages.value[idx].reactions = reactions;
    }
  } catch (error: any) {
    console.error("Failed to remove reaction:", error);
    $toast?.error(error.response?.data?.message || "Failed to remove reaction");
  }
}

async function handleCreateChat(request: any) {
  try {
    const response = await createChat(request);

    if (response?.data) {
      const newConversation = response.data;
      $toast?.success("Chat created successfully");
      showStartChatModal.value = false;

      // Refetch conversations
      await refetchConversations();

      if (newConversation.id) {
        // Wait a bit for the query to update
        await new Promise((resolve) => setTimeout(resolve, 500));

        const createdConv = whatsAppConversations.value.find((c) => c.id === newConversation.id);
        if (createdConv) {
          selectedWhatsAppConversationId.value = createdConv.id;
          selectedWhatsAppConversation.value = {
            ...createdConv,
            channel: "whatsapp",
          };
          showWhatsAppDetailView.value = true;
        } else {
          try {
            const chatResponse = await Unipile.getChat(newConversation.id);
            if (chatResponse.data?.data) {
              const chat = chatResponse.data.data as any;
              const contactName = chat.name || chat.title || "Unknown";
              selectedWhatsAppConversationId.value = chat.id;
              selectedWhatsAppConversation.value = {
                ...chat,
                id: chat.id,
                channel: "whatsapp",
                contactName: contactName,
                title: contactName,
                lastMessage: chat.lastMessage || "",
                lastMessageAt: chat.timestamp || new Date().toISOString(),
                unreadCount: chat.unread_count || 0,
                participants: chat.participants || [],
              } as Conversation;
              showWhatsAppDetailView.value = true;
            }
          } catch (error) {
            console.error("Failed to fetch created chat:", error);
          }
        }
      }
    }
  } catch (error: any) {
    console.error("Failed to create chat:", error);
    const errorMessage = error.response?.data?.message || error.message || "Failed to create chat";
    $toast?.error(errorMessage);
  }
}

async function handleSendMessage(data: {
  text?: string;
  attachments?: File[];
  voice_message?: File;
  video_message?: File;
}) {
  await showUsageBasedFeaturesModalOnce("whatsapp_message", async () => {
    const conversation = computedSelectedWhatsAppConversation.value;

    if (!conversation || !conversation.id) {
      $toast?.error("No conversation selected");
      return;
    }

    if (isSendingMessage.value) {
      return;
    }

    const channel = props.whatsAppChannel;
    if (!channel?.accountId) {
      $toast?.error("WhatsApp channel not connected");
      return;
    }

    // Check for files
    const hasAttachments =
      data.attachments && Array.isArray(data.attachments) && data.attachments.length > 0;
    const hasVoice = data.voice_message && data.voice_message instanceof File;
    const hasVideo = data.video_message && data.video_message instanceof File;
    const hasFiles = hasAttachments || hasVoice || hasVideo;

    // Handle text content
    const textContent = data.text || "";
    const isEmptyText = isBodyEmpty(textContent);

    if (!hasFiles && isEmptyText) {
      return;
    }

    const cleanedContent = isEmptyText ? "" : textContent;

    try {
      await sendMessage({
        conversationId: conversation.id,
        text: cleanedContent || undefined,
        quote_id: replyingTo.value?.id || (replyingTo.value as any)?.unipileMessageId || undefined,
        attachments: data.attachments,
        voice_message: data.voice_message,
        video_message: data.video_message,
      });

      replyingTo.value = null;
      emit("message-sent");
    } catch (error: any) {
      console.error("Failed to send message:", error);
      if (!error?.absorbedButRecoverable) {
        $toast?.error(error.response?.data?.message || "Failed to send message");
      }
    }
  });
}

// Webhooks setup
useWhatsAppMessageWebhooks({
  selectedConversation: computed(() => computedSelectedWhatsAppConversation.value),
  messages: whatsappMessages,
});

// Watchers
watch(
  () => props.whatsAppChannel,
  (newChannel) => {
    if (newChannel) {
      refetchConversations();
    }
  },
  { immediate: true }
);

watch(
  () => props.selectedBucket,
  () => {
    if (props.whatsAppChannel) {
      refetchConversations();
    }
  },
  { immediate: true }
);

// Emit refresh when conversations are loaded
watch(
  () => conversationsData.value,
  () => {
    if (conversationsData.value) {
      emit("refresh-conversations");
    }
  }
);

watch(
  () => props.pendingCueCardContent,
  async (payload) => {
    if (payload && payload.channel === "whatsapp" && payload.content) {
      if (showWhatsAppDetailView.value && computedSelectedWhatsAppConversation.value) {
        await applyWhatsAppContent(payload.content);
      } else {
        initialModalMessage.value = payload.content;
        showStartChatModal.value = true;
      }
      emit("content-applied");
    }
  }
);

// Watch initialConversationId to auto-select conversation, e.g. from clicking an
// Interaction History entry. The target conversation may not be in the currently
// loaded/paginated list (older chats, or the list simply hasn't loaded yet), so once
// the list has settled without a match, fetch that one conversation directly by id.
let lastAppliedWhatsAppConversationId: string | null = null;
watch(
  [() => whatsAppConversations.value, () => props.initialConversationId, isLoadingConversations],
  async ([conversations, initialId, loadingConversations]) => {
    if (!initialId || initialId === lastAppliedWhatsAppConversationId) return;

    const match = conversations?.find((c) => c.unipileChatId === initialId || c.id === initialId);
    if (match) {
      handleWhatsAppConversationSelected(match);
      lastAppliedWhatsAppConversationId = initialId;
      return;
    }

    if (loadingConversations) return;
    const accountId = props.whatsAppChannel?.accountId;
    if (!accountId) return;

    try {
      const response = await Unipile.getChat(initialId, accountId);
      if (response?.data?.data && initialId === props.initialConversationId) {
        handleWhatsAppConversationSelected(response.data.data as any);
        lastAppliedWhatsAppConversationId = initialId;
      }
    } catch (error) {
      console.error("Failed to load WhatsApp conversation from Interaction History", error);
    }
  },
  { immediate: true }
);
</script>

<style scoped>
.call-panel-body {
  padding: 12px;
  display: flex;
  flex-direction: column;
}

.whatsapp-layout-with-sidebar {
  width: 100%;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.whatsapp-sidebar-header {
  padding: 0.5rem 1rem;
  border-bottom: 1px solid #e5e7eb;
  background: #fff;
  flex-shrink: 0;
  margin-bottom: 6px;
}

.whatsapp-sidebar-header .ui-button {
  width: 100%;
  background-color: #25d366;
  border-color: #25d366;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
}

.whatsapp-sidebar-header .ui-button:hover:not(.ui-button--disabled) {
  background-color: #20ba5a;
  border-color: #20ba5a;
}

.whatsapp-sidebar-header .ui-button .ui-button__icon .iconify {
  font-size: 18px !important;
}

.whatsapp-empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #7b7fa8;
  font-size: 0.875rem;
  flex: 1;
  min-height: 0;
}

.whatsapp-empty-state--no-conversations {
  margin: 0;
  padding: 80px 40px;
  flex: 1;
  min-height: 0;
}

.whatsapp-empty-state__content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  max-width: 400px;
  gap: 16px;
}

.whatsapp-empty-state__icon {
  font-size: 3.5rem;
  color: #25d366;
  margin-bottom: 8px;
}

.whatsapp-empty-state__title {
  font-size: 1rem;
  font-weight: 500;
  color: #111827;
  margin: 0;
  line-height: 1.4;
}

.whatsapp-empty-state__message {
  font-size: 0.8125rem;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;
}

.whatsapp-message-view-wrapper {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.whatsapp-message-view-wrapper :deep(.whatsapp-thread-card) {
  flex: 1 1 0%;
  min-height: 0;
  overflow: hidden;
}

.whatsapp-message-view-wrapper :deep(.message-composer) {
  flex: 0 0 auto;
}

.back-to-list-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: transparent;
  border: none;
  color: #6b7280;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 12px;
  margin-left: -12px;
}

.back-to-list-btn:hover {
  color: #111827;
}

.back-to-list-btn svg {
  font-size: 20px;
}

.whatsapp-layout-with-sidebar :deep(.conversation-list) {
  width: 100%;
  min-width: 100%;
  border-right: none;
}

.whatsapp-content-wrapper {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.whatsapp-sidebar-view {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.whatsapp-main-content {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.whatsapp-disconnected-state {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1.5rem;
  min-height: 200px;
  width: 100%;
}

.whatsapp-disconnected-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 240px;
}

.whatsapp-disconnected-icon {
  font-size: 2rem;
  color: #d1d5db;
  margin-bottom: 0.75rem;
}

.whatsapp-disconnected-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 0.375rem 0;
}

.whatsapp-disconnected-message {
  font-size: 0.75rem;
  color: #6b7280;
  line-height: 1.5;
  margin: 0 0 1rem 0;
}

.whatsapp-disconnected-action {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 0.875rem;
  background: #3e3efe;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
}

.whatsapp-disconnected-action:hover {
  background: #3230d6;
}

.whatsapp-disconnected-action .iconify {
  font-size: 0.875rem;
}

.whatsapp-loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1.5rem;
  min-height: 200px;
  width: 100%;
  flex: 1;
}
</style>
